import Drawer from '@/common/components/Drawer/Drawer';
import { LelyCheckbox } from '@/common/components';
import { useLelyAuth } from '@/auth/useLelyAuth';
import { PREFERRED_SYSTEMS } from '@/common/constants';
import { useAppSelector } from '@/reducers/rootReducer';
import {
  setPreferredSystems,
  userDetailsSelectors,
} from '@/reducers/userDetailsSlice';
import { useGetAvailableSystemsQuery, System } from '@/rtk/availableSystemApi';
import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { SettingsDrawerProps } from '../ManageSettings';
import { lelyTokenPrimitives } from '@/common/styles/newStyles/LelyMuiTheme';

const t = lelyTokenPrimitives as Record<string, string>;

const getSystemIds = (systems: System[] = []) =>
  systems
    .map((system) => system.id)
    .sort()
    .join(',');

const ManageDevicePreferenceDrawer = ({
  open,
  onClose,
  onCloseAll,
}: SettingsDrawerProps) => {
  const dispatch = useDispatch();
  const { user } = useLelyAuth();
  const savedSystems =
    useAppSelector(userDetailsSelectors.selectPreferredSystems) ?? [];
  const [draftSystems, setDraftSystems] = useState<System[]>(savedSystems);
  const { data: availableSystems = [] } = useGetAvailableSystemsQuery(
    undefined,
    {
      skip: !user?.allowltda,
      refetchOnMountOrArgChange: true,
    },
  );

  useEffect(() => {
    if (open) setDraftSystems(savedSystems ?? []);
  }, [open, savedSystems]);

  const hasUnsavedChanges =
    getSystemIds(draftSystems) !== getSystemIds(savedSystems);

  const handleSystemToggle = (systemId: string) => {
    setDraftSystems((prev = []) => {
      if (prev.some((system) => system.id === systemId)) {
        return prev.filter((system) => system.id !== systemId);
      }

      const systemToAdd = availableSystems.find(
        (system) => system.id === systemId,
      );

      return systemToAdd ? [...prev, systemToAdd] : prev;
    });
  };

  const handleDevicePreferenceApply = () => {
    if (draftSystems.length === 0 || !user?.email) return;

    dispatch(setPreferredSystems(draftSystems));
    localStorage.setObject(`${PREFERRED_SYSTEMS}_${user.email}`, draftSystems);
    onClose();
  };

  const sortedSystems = [...availableSystems].sort((a, b) =>
    a.description.localeCompare(b.description),
  );

  return (
    <Drawer
      title="Device preference"
      open={open}
      onApply={handleDevicePreferenceApply}
      onClose={onClose}
      onCloseAll={onCloseAll}
      disableApply={draftSystems.length === 0}
      showFooter={true}
      showBackButton={true}
      invisibleBackdrop
      hasUnsavedChanges={hasUnsavedChanges}
    >
      <Box display="flex" flexDirection="column" gap={t.spaceSm}>
        <Typography variant="body2" color="text.primary">
          Select which devices are visible throughout the application.
        </Typography>
        <Box display="flex" flexDirection="column" gap={0}>
          {sortedSystems.map((system) => (
            <Box
              key={system.id}
              sx={{
                boxSizing: 'border-box',
                height: t.space8xl,
                padding: t.spaceMd,
                alignItems: 'center',
                gap: t.spaceSm,
                marginLeft: 0,
                marginRight: 0,
              }}
            >
              <LelyCheckbox
                checked={draftSystems.some(
                  (selectedSystem) => selectedSystem.id === system.id,
                )}
                dense
                label={system.description}
                name={system.id}
                onChange={() => handleSystemToggle(system.id)}
              />
            </Box>
          ))}
        </Box>
        {draftSystems.length === 0 && (
          <Typography variant="body2" color="error">
            No robot selected. Please make sure to select at least one robot.
          </Typography>
        )}
      </Box>
    </Drawer>
  );
};

export default ManageDevicePreferenceDrawer;
